import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppHeader from '../../components/AppHeader';
import { tripsApi, WRITEOFF_REASONS } from '../../api';

export default function TripSettle() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [returns, setReturns] = useState({});       // cargoId -> vráceno
  const [reasons, setReasons] = useState({});       // cargoId -> důvod
  const [boxQty, setBoxQty] = useState({});         // container_item_id -> spotřebováno
  const [boxReason, setBoxReason] = useState({});   // container_item_id -> důvod
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    tripsApi.settlement(id).then((r) => {
      setData(r.data);
      const ret = {};
      for (const c of r.data.cargo) if (!c.container_id && !c.settled_at) ret[c.id] = c.quantity;
      setReturns(ret);
    }).catch((e) => setError(e.response?.data?.error || 'Nelze načíst vyúčtování.'));
  }, [id]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const cargo = data.cargo.filter((c) => !c.settled_at).map((c) => c.container_id
        ? { cargo_id: c.id, returned_qty: 1 }                              // box se vrací celý
        : { cargo_id: c.id, returned_qty: Number(returns[c.id] ?? c.quantity), reason: reasons[c.id] || 'consumed' });
      const box_items = [];
      for (const b of data.boxes) for (const it of b.items) {
        const q = Number(boxQty[it.id] || 0);
        if (q > 0) box_items.push({ container_item_id: it.id, quantity: q, reason: boxReason[it.id] || 'consumed' });
      }
      const r = await tripsApi.settle(id, { cargo, box_items });
      setDone(r.data);
    } catch (err) { setError(err.response?.data?.error || 'Vyúčtování selhalo.'); }
    finally { setSaving(false); }
  };

  if (error && !data) return <div className="container" style={{ paddingTop: 40 }}><div className="alert alert-warning">{error}</div></div>;
  if (!data) return <div className="container" style={{ paddingTop: 48 }}><div className="spinner" /></div>;

  if (done) {
    return (
      <div className="container text-center" style={{ paddingTop: 60, maxWidth: 520 }}>
        <div style={{ fontSize: 56 }}>✅</div>
        <h2>Vyúčtováno</h2>
        <p className="mt-8">Vráceno na sklad: <strong>{done.returned}</strong> · zapsaných odpisů: <strong>{done.writeoffs}</strong></p>
        <button className="btn btn-primary mt-24" onClick={() => navigate(`/cesty/${id}`)}>Zpět na cestu</button>
      </div>
    );
  }

  const loose = data.cargo.filter((c) => !c.container_id);
  const unsettled = loose.filter((c) => !c.settled_at);

  return (
    <div>
      <AppHeader subtitle="Vyúčtování po návratu" />
      <div className="container" style={{ paddingTop: 24, maxWidth: 640 }}>
        <p className="text-muted mb-16">Zadej, kolik se z každé položky vrátilo na sklad. Zbytek se odepíše s vybraným důvodem. Boxy se vrací celé; u jejich obsahu zadej, co se spotřebovalo.</p>

        {error && <div className="alert alert-warning mb-16">{error}</div>}

        {/* Volné a vlastní položky */}
        {unsettled.length > 0 && (
          <div className="card mb-16">
            <h3 className="mb-12">Položky</h3>
            {unsettled.map((c) => {
              const ret = Number(returns[c.id] ?? c.quantity);
              const consumed = Math.max(0, c.quantity - ret);
              return (
                <div key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
                  <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{c.item_name}</div>
                      <div className="text-muted" style={{ fontSize: '0.8rem' }}>doručeno {c.quantity} {c.unit}{c.source_inventory_id ? ' · ze skladu' : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <label style={{ fontSize: '0.78rem' }} className="text-muted">vráceno</label>
                      <input type="number" min="0" max={c.quantity} step="any" className="form-input" style={{ width: 90, height: 36 }}
                        value={returns[c.id] ?? c.quantity} onChange={(e) => setReturns({ ...returns, [c.id]: e.target.value })} />
                    </div>
                  </div>
                  {consumed > 0 && (
                    <div className="flex gap-8" style={{ alignItems: 'center', marginTop: 6 }}>
                      <span className="badge badge-out">odpis {consumed} {c.unit}</span>
                      <select className="form-input" style={{ height: 34, width: 'auto', flex: 1 }} value={reasons[c.id] || 'consumed'} onChange={(e) => setReasons({ ...reasons, [c.id]: e.target.value })}>
                        {Object.entries(WRITEOFF_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Boxy a jejich obsah */}
        {data.boxes.map((b) => (
          <div key={b.cargo_id} className="card mb-16">
            <h3 className="mb-8">📦 {b.name} <span className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 400 }}>— vrací se celý</span></h3>
            {b.items.length === 0 ? <p className="text-muted" style={{ fontSize: '0.85rem' }}>Box je prázdný.</p> : b.items.map((it) => {
              const q = Number(boxQty[it.id] || 0);
              return (
                <div key={it.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                  <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1 }}>{it.item_name} <span className="text-muted" style={{ fontSize: '0.8rem' }}>({it.quantity} {it.unit})</span></div>
                    <div style={{ textAlign: 'right' }}>
                      <label style={{ fontSize: '0.78rem' }} className="text-muted">spotřebováno</label>
                      <input type="number" min="0" max={it.quantity} step="any" className="form-input" style={{ width: 90, height: 36 }}
                        value={boxQty[it.id] || ''} placeholder="0" onChange={(e) => setBoxQty({ ...boxQty, [it.id]: e.target.value })} />
                    </div>
                  </div>
                  {q > 0 && (
                    <div className="flex gap-8" style={{ alignItems: 'center', marginTop: 6 }}>
                      <span className="badge badge-out">odpis {q} {it.unit}</span>
                      <select className="form-input" style={{ height: 34, width: 'auto', flex: 1 }} value={boxReason[it.id] || 'consumed'} onChange={(e) => setBoxReason({ ...boxReason, [it.id]: e.target.value })}>
                        {Object.entries(WRITEOFF_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {unsettled.length === 0 && data.boxes.length === 0 && (
          <p className="text-muted text-center">Žádné nevyúčtované doručené položky.</p>
        )}

        <div className="flex gap-8 mt-16">
          <button className="btn btn-primary flex-1" disabled={saving} onClick={submit}>{saving ? 'Ukládám…' : 'Uložit vyúčtování'}</button>
          <button className="btn btn-outline" onClick={() => navigate(`/cesty/${id}`)}>Zpět</button>
        </div>
      </div>
    </div>
  );
}
